/**
 * Twin mapping for placed antennas. The twin data layer resolves `noaa:<id>`
 * from /data/loader/antennas-noaa.json, where a placed antenna never is, but it
 * answers from its runtime registry first (registerAssetSpec in
 * src/features/twin/data). syncPlacedTwins keeps that registry in step with the
 * placed antennas of the dataset: each gets the existing parametric
 * assetSpecForAntenna under `noaa:<placed id>`, so selecting that asset in
 * Maintenance loads its twin; removed ones are unregistered.
 */
import {
  NOAA_ASSET_PREFIX,
  assetSpecForAntenna,
  registerAssetSpec,
  unregisterAssetSpec,
} from '@/features/twin/data';
import type { AssetSpec, ProceduralAntennaSpec } from '@/features/twin/types';
import { useLoader } from '../store';
import type { Antenna } from '../types';
import { isPlacedAntenna } from './placement';

/** The twin asset id for a placed antenna: `noaa:<antennaId>`, as assetSpecForAntenna names it. */
export function placedAssetId(antennaId: string): string {
  return `${NOAA_ASSET_PREFIX}${antennaId}`;
}

/** The placed antenna an asset id refers to, from the loader dataset; null for anything else. */
export function placedAntennaForAsset(assetId: string): Antenna | null {
  if (!assetId.startsWith(NOAA_ASSET_PREFIX)) return null;
  const antennaId = assetId.slice(NOAA_ASSET_PREFIX.length);
  const antenna = useLoader.getState().dataset?.antennas.find((a) => a.id === antennaId);
  return antenna && isPlacedAntenna(antenna) ? antenna : null;
}

/** The parametric twin spec of a placed antenna in the dataset, or null when the asset is not one. */
export function placedAssetSpec(assetId: string): (AssetSpec & { procedural: ProceduralAntennaSpec }) | null {
  const antenna = placedAntennaForAsset(assetId);
  return antenna ? assetSpecForAntenna(antenna) : null;
}

/** Asset id to the record its registered spec was built from (only the ones this module registered). */
const registered = new Map<string, Antenna>();

/**
 * Register a twin spec for every placed antenna in `antennas` (null: none) and
 * unregister the ones registered earlier that are no longer there. A record
 * that changed under the same id is registered again. Throws, with the schema
 * issues, when the twin registry rejects a spec; specs registered before the
 * failing one stay registered.
 */
export function syncPlacedTwins(antennas: readonly Antenna[] | null): void {
  const wanted = new Map<string, Antenna>();
  for (const a of antennas ?? []) if (isPlacedAntenna(a)) wanted.set(placedAssetId(a.id), a);
  for (const assetId of [...registered.keys()]) {
    if (wanted.has(assetId)) continue;
    unregisterAssetSpec(assetId);
    registered.delete(assetId);
  }
  for (const [assetId, antenna] of wanted) {
    if (registered.get(assetId) === antenna) continue;
    registerAssetSpec(assetId, assetSpecForAntenna(antenna));
    registered.set(assetId, antenna);
  }
}
